import { EventName } from "module/analytics/types"
import { RiskLevel, useAccountBalanceStrings } from "module/vault/hook/useAccountBalanceStrings"
import { formatNumberUSD } from "util/format"

import { Box, Divider, HStack, Icon, SimpleGrid, Spacer, Text, VStack } from "@chakra-ui/react"
import { Liquidity } from "@perp/sdk-curie"
import Big from "big.js"
import { RankingBadgeBronze, RankingBadgeDiamond, RankingBadgeGold, RankingBadgeSilver } from "component/Icon"
import { MagicGroup } from "component/MagicGroup"
import { MarketListIcon } from "component/MarketListIcon"
import { MotionHStack } from "component/Motion"
import { PerpLink } from "component/PerpLink"
import { Stats } from "component/Stats"
import { capitalize } from "lodash-es"
import { useCallback } from "react"
import { RiArrowLeftLine, RiBubbleChartLine, RiContrastDrop2Fill, RiHandCoinLine, RiSkull2Line } from "react-icons/ri"
import { usePositionMaker } from "sdk-react/clearingHouse/usePositionMaker"

import { useLiquidity } from "../../hook/useLiquidity"
import { useLiquidityProviderData } from "../../hook/useLiquidityProviderData"
import { useRankingTier } from "../../hook/useRankingTier"
import { MakerPositionNetReturn } from "./MakerPositionNetReturn"
import { MakerPositionPnl } from "./MakerPositionPnl"
import { MakerPositionSize } from "./MakerPositionSize"

export interface PoolSummaryProps {
    tickerSymbol: string
    liquidityList: Liquidity[]
}

const PLACEHOLDER = "-,--"

const RiskColorMap = {
    [RiskLevel.LOW]: "green.400",
    [RiskLevel.MEDIUM]: "yellow.400",
    [RiskLevel.HIGH]: "red.400",
}
export function getRankBadge(rank: string) {
    switch (rank) {
        case "diamond":
            return RankingBadgeDiamond
        case "gold":
            return RankingBadgeGold
        case "silver":
            return RankingBadgeSilver
        case "bronze":
            return RankingBadgeBronze
    }
}

export function getRankColor(rank: string) {
    switch (rank) {
        case "diamond":
            return "perpetual.100"
        case "gold":
            return "gold"
        case "silver":
            return "silver"
        case "bronze":
            return "#cd7f32"
    }
}

function getRankDesc(rank: string) {
    switch (rank) {
        case "diamond":
            return "Hashtag boss!"
        case "gold":
            return "Outstanding!"
        case "silver":
            return "Not too shabby!"
        case "bronze":
            return "Playing it safe."
    }
}
const RankIndicator = () => (
    <>
        <Spacer />
        <MotionHStack
            animate={{
                x: [-2, 2],
                transition: {
                    repeat: Infinity,
                    duration: 0.6,
                    repeatType: "mirror",
                    type: "spring",
                },
            }}
        >
            <Icon boxSize="20px" as={RiArrowLeftLine} />
            <Text fontSize="xs">I'm here!</Text>
        </MotionHStack>
    </>
)
export function LPSummary({ tickerSymbol }: PoolSummaryProps) {
    const { totalPendingFee, totalLiquidity } = useLiquidity(tickerSymbol)

    const totalPendingFeeStr = totalPendingFee
        ? formatNumberUSD({ num: totalPendingFee, displayLength: 2 })
        : PLACEHOLDER

    const makerPosition = usePositionMaker(tickerSymbol)

    const totalLiquidityStr = totalLiquidity ? formatNumberUSD({ num: totalLiquidity }) : PLACEHOLDER
    const {
        personalBaseAprStr,
        personalRewardsAprStr,
        marketTotalLowerAprVal,
        marketTotalUpperAprVal,
        personalTotalAprStr,
        personalTotalAprVal,
    } = useLiquidityProviderData()
    const { marginRatioStr, riskLevel, leverageStr } = useAccountBalanceStrings()

    const { tickOne, tickTwo, tickThree, rankRangeOneStr, rankRangeTwoStr, rankRangeThreeStr, rankRangeFourStr } =
        useRankingTier(marketTotalLowerAprVal, marketTotalUpperAprVal)

    const getRank = useCallback(() => {
        if (new Big(personalTotalAprVal).gt(tickThree)) {
            return "diamond"
        } else if (new Big(personalTotalAprVal).gt(tickTwo)) {
            return "gold"
        } else if (new Big(personalTotalAprVal).gt(tickOne)) {
            return "silver"
        } else {
            return "bronze"
        }
    }, [personalTotalAprVal, tickThree, tickTwo, tickOne])

    return (
        <Box py={6}>
            <VStack w="full" align="flex-start" spacing={6}>
                <HStack w="full">
                    <Text fontSize="md" fontWeight="semibold">
                        Provided Liquidity
                    </Text>
                    <Spacer />
                </HStack>
                <SimpleGrid spacing={6} columns={{ base: 1, md: 2 }} w="100%">
                    <MagicGroup isShareable>
                        <Stats
                            size="xl"
                            label="Total APR"
                            numberColor={"white"}
                            number={personalTotalAprStr}
                            icon={<Icon color={getRankColor(getRank())} as={getRankBadge(getRank())} />}
                            helper={`${capitalize(getRank())} - ${getRankDesc(getRank())}`}
                        />
                        <SimpleGrid spacing={6} columns={2}>
                            <Stats
                                size="xl"
                                label="Base APR"
                                showTooltip={true}
                                number={`${personalBaseAprStr}`}
                                numberColor={"green.400"}
                                helper="Est. APR from trading fees"
                            />
                            <Stats
                                size="xl"
                                showTooltip={true}
                                label="Rewards APR"
                                number={`${personalRewardsAprStr}`}
                                numberColor={"green.400"}
                                helper="Your est. APR from Pool Party rewards"
                            />
                        </SimpleGrid>
                        <Divider />
                        <HStack spacing={3} py={2}>
                            <MarketListIcon boxSize="24px" coin={tickerSymbol.replace("USD", "")} />

                            <Text fontSize="md" fontWeight={"semibold"}>
                                {tickerSymbol} - APR Ranking
                            </Text>
                        </HStack>
                        <HStack spacing={3}>
                            <Icon color="perpetual.100" boxSize="24px" as={RankingBadgeDiamond} />
                            <Box>
                                <Text fontSize="sm" fontWeight="bold">
                                    Diamond
                                </Text>
                                <Text fontSize="xs">{rankRangeFourStr}</Text>
                                <Text fontSize="xs" opacity={0.65}>
                                    Hashtag boss!
                                </Text>
                            </Box>
                            {getRank() === "diamond" ? <RankIndicator /> : null}
                        </HStack>
                        <HStack spacing={3}>
                            <Icon color="gold" boxSize="24px" as={RankingBadgeGold} />
                            <Box>
                                <Text fontSize="sm" fontWeight="bold">
                                    Gold
                                </Text>
                                <Text fontSize="xs">{rankRangeThreeStr}</Text>
                                <Text fontSize="xs" opacity={0.65}>
                                    Outstanding!
                                </Text>
                            </Box>
                            {getRank() === "gold" ? <RankIndicator /> : null}
                        </HStack>
                        <HStack spacing={3}>
                            <Icon color="silver" boxSize="24px" as={RankingBadgeSilver} />
                            <Box>
                                <Text fontSize="sm" fontWeight="bold">
                                    Silver
                                </Text>
                                <Text fontSize="xs">{rankRangeTwoStr}</Text>
                                <Text fontSize="xs" opacity={0.65}>
                                    Not too shabby!
                                </Text>
                            </Box>
                            {getRank() === "silver" ? <RankIndicator /> : null}
                        </HStack>
                        <HStack spacing={3}>
                            <Icon color="#cd7f32" boxSize="24px" as={RankingBadgeBronze} />
                            <Box>
                                <Text fontSize="sm" fontWeight="bold">
                                    Bronze
                                </Text>
                                <Text fontSize="xs">{rankRangeOneStr}</Text>
                                <Text fontSize="xs" opacity={0.65}>
                                    Playing it safe.
                                </Text>
                            </Box>
                            {getRank() === "bronze" ? <RankIndicator /> : null}
                        </HStack>{" "}
                        <Divider data-html2canvas-ignore />
                    </MagicGroup>

                    {makerPosition ? (
                        <MakerPositionNetReturn
                            fees={totalPendingFee ? (totalPendingFee as unknown as string) : "0"}
                            position={makerPosition}
                        />
                    ) : (
                        <Stats
                            // TODO: this is to handle the case where makerPosition is null. We should do it within MakerPositionNetReturn component
                            icon={<Icon color="purple.400" as={RiHandCoinLine} />}
                            showTooltip={true}
                            size="xl"
                            label="Net Return"
                            number={"$--"}
                            numberColor={"white"}
                            helper="Total Fees - Impermanent Loss"
                        />
                    )}
                </SimpleGrid>
                <Divider />

                <SimpleGrid columns={{ base: 1, md: 2 }} width="100%" spacing={6}>
                    <Stats
                        icon={<Icon color="perpetual.500" as={RiContrastDrop2Fill} />}
                        size="xl"
                        width="100%"
                        label="Total Liquidity"
                        textTransform="lowercase"
                        number={`$${totalLiquidityStr}`}
                        showTooltip={true}
                        helper="Provided liquidity (leveraged)"
                    />
                    <Stats
                        size="xl"
                        numberColor={RiskColorMap[riskLevel]}
                        label="margin ratio"
                        number={marginRatioStr}
                        helper={"Leverage: " + leverageStr}
                    />

                    <Stats
                        icon={<Icon color="yellow.500" as={RiBubbleChartLine} />}
                        showTooltip={true}
                        size="xl"
                        label="Total Fees"
                        number={`$${totalPendingFeeStr}`}
                        numberColor={"white"}
                        helper="Total fees earned from current liquidity. Added to Free Collateral automatically."
                    />
                    {makerPosition ? (
                        <MagicGroup>
                            <MakerPositionPnl position={makerPosition} />
                            <Text fontSize="sm" opacity={0.65}>
                                The estimated IL may be inaccurate if you have a very large liquidity position.
                            </Text>
                            <Divider />
                            <MakerPositionSize position={makerPosition} />
                            <Text fontSize="sm" opacity={0.65}>
                                Dynamic position resulting from providing liquidity. You can close the position after
                                removing liquidity.
                                <br /> <br />
                                <PerpLink
                                    href="https://support.perp.com/hc/en-us/articles/5257492022809"
                                    trackingProps={{ eventName: EventName.LINK_SUPPORT }}
                                >
                                    <Text color="perpetual.400">What is an Impermanent Position?</Text>
                                </PerpLink>
                            </Text>
                        </MagicGroup>
                    ) : (
                        <Stats
                            icon={<Icon color="red.300" as={RiSkull2Line} />}
                            label={"Impermanent Loss"}
                            helper="Unrealized PnL from your impermanent position"
                            number={"$--"}
                            showTooltip={true}
                            size="xl"
                        />
                    )}
                </SimpleGrid>
                <Divider />
            </VStack>
        </Box>
    )
}
